import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  WorkflowIcon, 
  DatabaseIcon, 
  WrenchIcon, 
  ShieldCheckIcon,
  CheckCircleIcon
} from './Icons';

const PillarsSection = ({ pillars }) => {
  const [activeTab, setActiveTab] = useState(pillars[0].id);
  const activePillar = pillars.find(p => p.id === activeTab) || pillars[0];
  const { title, subtitle, description, points } = activePillar;

  // Render high-fidelity interactive visual representation for each technology pillar
  const renderVisualRepresentation = (tabId) => {
    if (tabId === 'orchestration') {
      return (
        <div className="relative w-full h-80 rounded-2xl bg-slate-900/90 border border-slate-800 p-6 flex flex-col justify-between overflow-hidden shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-tr from-brand-600/10 via-transparent to-accent-500/10 pointer-events-none" />
          
          {/* Top Status Bar */}
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-brand-400 animate-ping" />
              <span className="text-[11px] font-mono text-brand-300 font-semibold">
                orchestrator.plan(DAG_EXECUTION)
              </span>
            </div>
            <span className="text-[10px] bg-brand-500/20 text-brand-300 border border-brand-500/30 px-2 py-0.5 rounded-full font-mono">
              Topological Engine
            </span>
          </div>

          {/* Interactive DAG Graph Visualization */}
          <div className="flex-1 flex items-center justify-between relative px-2 sm:px-6">
            {/* Step 1: Planner Node */}
            <div className="flex flex-col items-center z-10">
              <div className="w-14 h-14 rounded-2xl bg-violet-950/80 border border-violet-500/50 flex flex-col items-center justify-center shadow-lg shadow-violet-500/20 text-center">
                <WorkflowIcon className="w-6 h-6 text-violet-400" />
                <span className="text-[8px] font-mono text-violet-200 mt-0.5">PLANNER</span>
              </div>
              <span className="text-[10px] text-slate-400 mt-1 font-mono">Step 1</span>
            </div>

            {/* Connecting Flow Line 1 */}
            <div className="flex-1 h-0.5 bg-slate-800 relative mx-2">
              <motion.div 
                className="absolute top-0 bottom-0 w-8 bg-gradient-to-r from-transparent via-violet-400 to-transparent"
                animate={{ x: [0, 80] }}
                transition={{ repeat: Infinity, duration: 1.8, ease: "linear" }}
              />
            </div>

            {/* Step 2: Parallel Branch (RAG + TOOL) */}
            <div className="flex flex-col gap-3 z-10">
              <div className="w-24 px-2 py-1.5 rounded-xl bg-cyan-950/80 border border-cyan-500/50 flex items-center gap-2 shadow-md">
                <DatabaseIcon className="w-4 h-4 text-cyan-400" />
                <div className="text-left">
                  <div className="text-[9px] font-mono font-bold text-cyan-200">RAG_AGENT</div>
                  <div className="text-[8px] text-cyan-400/80">Parallel KB</div>
                </div>
              </div>
              <div className="w-24 px-2 py-1.5 rounded-xl bg-emerald-950/80 border border-emerald-500/50 flex items-center gap-2 shadow-md">
                <WrenchIcon className="w-4 h-4 text-emerald-400" />
                <div className="text-left">
                  <div className="text-[9px] font-mono font-bold text-emerald-200">TOOL_AGENT</div>
                  <div className="text-[8px] text-emerald-400/80">SaaS API Call</div>
                </div>
              </div>
            </div>

            {/* Connecting Flow Line 2 */}
            <div className="flex-1 h-0.5 bg-slate-800 relative mx-2">
              <motion.div 
                className="absolute top-0 bottom-0 w-8 bg-gradient-to-r from-transparent via-rose-400 to-transparent"
                animate={{ x: [0, 80] }}
                transition={{ repeat: Infinity, duration: 1.8, delay: 0.5, ease: "linear" }}
              />
            </div>

            {/* Step 3: Critic Gatekeeper Node */}
            <div className="flex flex-col items-center z-10">
              <div className="w-14 h-14 rounded-2xl bg-rose-950/80 border border-rose-500/50 flex flex-col items-center justify-center shadow-lg shadow-rose-500/20 text-center">
                <ShieldCheckIcon className="w-6 h-6 text-rose-400" />
                <span className="text-[8px] font-mono text-rose-200 mt-0.5">CRITIC</span>
              </div>
              <span className="text-[10px] text-rose-400 mt-1 font-mono font-semibold">Audit Passed</span>
            </div>
          </div>

          {/* Footer Metrics */}
          <div className="text-[10px] font-mono text-slate-400 border-t border-slate-800 pt-3 flex items-center justify-between">
            <span>Execution Model: Directed Acyclic Graph (DAG)</span>
            <span className="text-emerald-400">Zero Cyclical Deadlocks</span>
          </div>
        </div>
      );
    }

    if (tabId === 'rag') {
      return (
        <div className="relative w-full h-80 rounded-2xl bg-slate-900/90 border border-slate-800 p-6 flex flex-col justify-between overflow-hidden shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-tr from-cyan-600/10 via-transparent to-blue-500/10 pointer-events-none" />
          
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <span className="text-[11px] font-mono text-cyan-300 font-semibold">
              elasticsearch.hybrid_vector_search()
            </span>
            <span className="text-[10px] bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 px-2 py-0.5 rounded-full font-mono">
              Tenant: tenant_alpha
            </span>
          </div>

          <div className="flex-1 flex items-center justify-around relative">
            {/* Raw Ingestion */}
            <div className="flex flex-col items-center bg-slate-800/80 border border-slate-700 p-3 rounded-2xl w-28 text-center shadow-md">
              <span className="text-2xl mb-1">📄</span>
              <span className="text-[10px] font-mono font-bold text-slate-200 truncate w-full">PolicyRules.pdf</span>
              <span className="text-[8px] text-slate-400 mt-0.5">Table & Text OCR</span>
            </div>

            {/* Ingestion Stream */}
            <div className="flex flex-col items-center">
              <span className="text-[9px] font-mono text-cyan-400 mb-1">Chunking (512 tokens)</span>
              <div className="w-16 h-0.5 bg-slate-700 relative">
                <motion.div 
                  className="w-3 h-3 rounded-full bg-cyan-400 absolute -top-1"
                  animate={{ left: ['0%', '100%'] }}
                  transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                />
              </div>
            </div>

            {/* Vector DB Nodes */}
            <div className="p-3.5 rounded-2xl bg-slate-800/90 border border-cyan-500/40 text-center shadow-lg shadow-cyan-500/10">
              <div className="text-[10px] font-mono font-bold text-cyan-300 mb-2">
                Elasticsearch Index
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <motion.div 
                    key={i}
                    animate={{ 
                      backgroundColor: ["rgba(15, 23, 42, 1)", "rgba(6, 182, 212, 0.4)", "rgba(15, 23, 42, 1)"]
                    }}
                    transition={{ duration: 1.8, delay: i * 0.25, repeat: Infinity }}
                    className="w-5 h-5 rounded-md border border-cyan-500/30 flex items-center justify-center text-[7px] font-mono text-cyan-200"
                  >
                    v{i}
                  </motion.div>
                ))}
              </div>
              <span className="text-[8px] text-slate-400 block mt-2 font-mono">dim: 1536</span>
            </div>
          </div>

          <div className="text-[10px] font-mono text-slate-400 border-t border-slate-800 pt-3 flex items-center justify-between">
            <span>Cosine Distance: 0.941</span>
            <span className="text-emerald-400">Strict Tenant Vector Segregation</span>
          </div>
        </div>
      );
    }

    if (tabId === 'tools') {
      return (
        <div className="relative w-full h-80 rounded-2xl bg-slate-900/90 border border-slate-800 p-6 flex flex-col justify-between overflow-hidden shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-tr from-emerald-600/10 via-transparent to-teal-500/10 pointer-events-none" />
          
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <span className="text-[11px] font-mono text-emerald-300 font-semibold">
              postman_onboarding &bull; Tool Sandbox
            </span>
            <span className="text-[10px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded-full font-mono">
              MVEL Protected
            </span>
          </div>

          <div className="flex-1 flex flex-col justify-center space-y-3 font-mono text-xs">
            <div className="p-3 rounded-xl bg-slate-950/80 border border-slate-800 text-slate-300">
              <div className="text-[10px] text-slate-500 mb-1">{"// 1. Auto-generate schema from Postman Collection"}</div>
              <span className="text-violet-400">POST</span> <span className="text-slate-200">/api/tools/onboard</span>
              <div className="text-[10px] text-slate-400 mt-1">Tools registered: [gmail.send, jira.create, stripe.refund]</div>
            </div>

            <div className="p-3 rounded-xl bg-emerald-950/40 border border-emerald-500/30 text-emerald-200">
              <div className="text-[10px] text-emerald-400 mb-1">{"// 2. Hardened Tool Dispatch"}</div>
              <span className="text-emerald-300 font-bold">TOOL_AGENT:</span> <span className="text-white">jira.create_ticket(</span>
              <span className="text-cyan-300">"Duplicate charge #ORD-9821"</span>
              <span className="text-white">)</span>
              <div className="text-[10px] text-emerald-400 mt-1 font-semibold">
                HTTP 201 Created &bull; Response latency: 142ms
              </div>
            </div>
          </div>

          <div className="text-[10px] font-mono text-slate-400 border-t border-slate-800 pt-3 flex items-center justify-between">
            <span>Sandboxed Memory Space</span>
            <span className="text-emerald-400">Cryptographically Signed HMAC</span>
          </div>
        </div>
      );
    }

    if (tabId === 'voice') {
      return (
        <div className="relative w-full h-80 rounded-2xl bg-slate-900/90 border border-slate-800 p-6 flex flex-col justify-between overflow-hidden shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-tr from-indigo-600/10 via-transparent to-pink-500/10 pointer-events-none" />
          
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <span className="text-[11px] font-mono text-indigo-300 font-semibold">
              voice-agent-gateway &bull; WebSocket Audio
            </span>
            <span className="text-[10px] bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 px-2 py-0.5 rounded-full font-mono">
              SIP / LiveKit Bridge
            </span>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center space-y-4">
            {/* Animated Audio Waveform */}
            <div className="flex items-center gap-1.5 h-16">
              {[12, 28, 48, 20, 60, 36, 16, 52, 64, 40, 24, 56, 32, 16, 44, 20].map((h, i) => (
                <motion.div
                  key={i}
                  className="w-1.5 rounded-full bg-gradient-to-t from-brand-500 to-accent-400"
                  animate={{ height: [`${h * 0.3}px`, `${h}px`, `${h * 0.3}px`] }}
                  transition={{ repeat: Infinity, duration: 1.2, delay: i * 0.08, ease: "easeInOut" }}
                />
              ))}
            </div>

            <div className="text-center space-y-1">
              <span className="text-xs font-mono font-bold text-white">
                Low Latency Audio Stream Active
              </span>
              <p className="text-[11px] text-slate-400 font-mono">
                Round-trip latency: <strong className="text-emerald-400">380ms</strong> &bull; Interruptible Speech Recognition
              </p>
            </div>
          </div>

          <div className="text-[10px] font-mono text-slate-400 border-t border-slate-800 pt-3 flex items-center justify-between">
            <span>Protocol: wss://agentexpert.in/voice/v1</span>
            <span className="text-emerald-400">Direct Agent Handoff</span>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <section id="pillars" className="py-20 md:py-32 bg-slate-100/50 dark:bg-slate-950/60 relative border-t border-b border-slate-200/80 dark:border-slate-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Section Heading */}
        <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-brand-500/10 text-brand-600 dark:text-brand-400 border border-brand-500/20 font-inter">
            <WorkflowIcon className="w-3.5 h-3.5" />
            <span>Under The Hood</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight font-outfit">
            Core Technology Architecture
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg font-light leading-relaxed">
            Engineered from ground up with Java Spring Boot, Elasticsearch vector search, and WebSocket streaming for enterprise resilience.
          </p>
        </div>

        {/* Apple-style Segmented Control */}
        <div className="flex justify-center mb-12">
          <div className="p-1.5 rounded-2xl glass-panel flex flex-wrap justify-center gap-1 shadow-md max-w-4xl border border-slate-200 dark:border-slate-800">
            {pillars.map((pillar) => {
              const Icon = pillar.Icon;
              const isActive = activeTab === pillar.id;
              return (
                <button
                  key={pillar.id}
                  onClick={() => setActiveTab(pillar.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 active:scale-95 ${
                    isActive 
                      ? 'bg-brand-600 text-white shadow-md shadow-brand-500/25' 
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/50 dark:hover:bg-slate-800/50'
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{pillar.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content Display */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left Details */}
          <motion.div 
            key={activeTab}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:col-span-6 space-y-6 text-left"
          >
            <div>
              <span className="text-xs font-mono font-bold text-brand-600 dark:text-brand-400 uppercase tracking-wider block mb-1">
                {subtitle}
              </span>
              <h3 className="text-2xl sm:text-4xl font-extrabold text-slate-900 dark:text-white font-outfit tracking-tight">
                {title}
              </h3>
            </div>

            <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed font-light">
              {description}
            </p>

            <div className="space-y-4 pt-2">
              {points.map((pt, idx) => (
                <div key={idx} className="flex items-start gap-3.5">
                  <div className="w-6 h-6 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircleIcon className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white font-outfit">
                      {pt.label}
                    </h4>
                    <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed font-light mt-0.5">
                      {pt.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right Visual Representation */}
          <motion.div 
            key={`vis-${activeTab}`}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35 }}
            className="lg:col-span-6"
          >
            {renderVisualRepresentation(activeTab)}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PillarsSection;
